/**
 * /client -- outcomes list + detail (comment-only, no edit -- see "Product
 * decision" in project-brain/pages/client-outcomes.md).
 *
 * Detail is a sub-view of the same route via ?issue=&owner=&repo= search
 * params (routing shape is an implementation choice per the page spec)
 * rather than a separate path segment, so back/forward navigation and
 * sharing a link both work without a second route. owner/repo are carried
 * alongside the issue number because issue numbers are only unique *within*
 * a repo -- once a project has more than one connected repo, the number
 * alone can't tell two different issues apart (see
 * project-brain/api/client-outcomes.md).
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Card from '@components/ui/Card';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import Modal from '@components/ui/Modal';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import ErrorState from '@components/ui/ErrorState';
import { renderSafeMarkdown } from '@lib/safe-markdown';
import './Outcomes.css';

// Mirrors functions/lib/outcome-template.ts's OUTCOME_SECTION_ORDER -- kept
// as a small duplicated constant rather than a cross-boundary import, since
// the frontend (Vite, rooted at src/) and functions/ (Pages Functions) are
// separate bundles. This is presentation labels only, not business logic.
const OUTCOME_SECTION_ORDER = [
  'Business Objective',
  'Success Criteria',
  'Acceptance Criteria',
  'Priority',
  'Estimated Effort',
  'Digital Maturity Impact',
  'Dependencies',
  'Evidence',
  'Notes',
] as const;

// Matches GitHub's default Project (Kanban) board column names -- see
// "Product decision" in project-brain/pages/client-outcomes.md for why this
// was changed from the earlier bespoke wording (Proposed/Approved/Waiting/
// Completed) to mirror the board exactly. No live sync with the board
// itself -- this is a naming alignment only, still driven by the issue's
// own `status: *` label.
const STATUS_COLUMNS = [
  { key: 'backlog', label: 'Backlog' },
  { key: 'ready', label: 'Ready' },
  { key: 'in-progress', label: 'In Progress' },
  { key: 'in-review', label: 'In Review' },
  { key: 'done', label: 'Done' },
] as const;
const OTHER_COLUMN_KEY = 'other';

interface OutcomeListItem {
  project_id: string;
  project_name: string;
  github_owner: string;
  github_repo: string;
  issue_number: number;
  title: string;
  status: string | null;
}

interface OutcomeComment {
  id: number;
  body: string;
  created_at: string;
}

interface OutcomeAttachment {
  id: string;
  file_name: string;
  mime_type: string;
  file_size_bytes: number;
  uploaded_by_name: string;
  created_at: string;
}

interface OutcomeDetail {
  project_id: string;
  project_name: string;
  github_owner: string;
  github_repo: string;
  issue_number: number;
  title: string;
  body: string;
  updated_at: string;
  comments: OutcomeComment[];
  attachments: OutcomeAttachment[];
}

interface RepoOption {
  project_id: string;
  project_name: string;
  github_owner: string;
  github_repo: string;
}

function repoOptionKey(r: RepoOption): string {
  return `${r.project_id}|${r.github_owner}|${r.github_repo}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Mirrors functions/lib/outcome-attachments-helpers.ts's isInlineViewable --
// what the browser can render directly (image viewer / built-in PDF
// viewer), matching the backend's Content-Disposition: inline decision.
function isImageMime(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}
function isInlineViewableMime(mimeType: string): boolean {
  return isImageMime(mimeType) || mimeType === 'application/pdf';
}

const KanbanBoard: React.FC<{ items: OutcomeListItem[]; onSelect: (item: OutcomeListItem) => void }> = ({
  items,
  onSelect,
}) => {
  const otherItems = items.filter((i) => !STATUS_COLUMNS.some((c) => c.key === i.status));
  const columns = [
    ...STATUS_COLUMNS.map((col) => ({ key: col.key, label: col.label, items: items.filter((i) => i.status === col.key) })),
    ...(otherItems.length > 0 ? [{ key: OTHER_COLUMN_KEY, label: 'Other', items: otherItems }] : []),
  ];

  // A project's board can span more than one connected repo -- only show
  // the repo tag on each card when that's actually the case here, so the
  // common single-repo project isn't cluttered with a redundant label.
  const distinctRepoCount = new Set(items.map((i) => i.github_repo)).size;

  return (
    <div className="client-outcomes-kanban">
      {columns.map((col) => (
        <div key={col.key} className="client-outcomes-kanban-column">
          <h3 className="client-outcomes-kanban-column-title">
            {col.label} <span className="client-outcomes-kanban-column-count">({col.items.length})</span>
          </h3>
          <div className="client-outcomes-kanban-column-cards">
            {col.items.length === 0 ? (
              <p className="client-outcomes-kanban-empty">—</p>
            ) : (
              col.items.map((item) => (
                <button
                  key={`${item.github_owner}/${item.github_repo}#${item.issue_number}`}
                  type="button"
                  className="client-outcomes-kanban-card"
                  onClick={() => onSelect(item)}
                >
                  {distinctRepoCount > 1 && (
                    <span className="client-outcomes-kanban-card-repo">{item.github_repo}</span>
                  )}
                  <span className="client-outcomes-row-number">#{item.issue_number}</span>
                  <span className="client-outcomes-row-title">{item.title}</span>
                </button>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const ProjectSection: React.FC<{
  projectId: string;
  projectName: string;
  items: OutcomeListItem[];
  onSelect: (item: OutcomeListItem) => void;
  onNewOutcome: (projectId: string, projectName: string) => void;
}> = ({ projectId, projectName, items, onSelect, onNewOutcome }) => (
  <div className="client-outcomes-project-section">
    <div className="client-outcomes-project-header">
      <h2 className="client-outcomes-section-heading">{projectName}</h2>
      <Button type="button" variant="secondary" size="sm" onClick={() => onNewOutcome(projectId, projectName)}>
        New outcome
      </Button>
    </div>
    {items.length === 0 ? (
      <p className="client-outcomes-empty">No open outcomes right now.</p>
    ) : (
      <KanbanBoard items={items} onSelect={onSelect} />
    )}
  </div>
);

const ClientOutcomes: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedIssue = searchParams.get('issue');
  const selectedOwner = searchParams.get('owner');
  const selectedRepo = searchParams.get('repo');

  const [outcomes, setOutcomes] = useState<OutcomeListItem[] | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [repoOptions, setRepoOptions] = useState<RepoOption[]>([]);
  const [repoOptionsLoading, setRepoOptionsLoading] = useState(true);

  const [detail, setDetail] = useState<OutcomeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  const [addAttachmentOpen, setAddAttachmentOpen] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  // Set after creating a new outcome if a staged file failed to attach --
  // the outcome itself still exists and isn't rolled back, so this is a
  // separate, non-blocking warning shown on the detail view it navigates to.
  const [postActionWarning, setPostActionWarning] = useState<string | null>(null);

  // Same jump-to-comment-box button on every screen size (not a
  // desktop/mobile split) -- avoids maintaining two different layouts for
  // the same underlying problem ("don't make me scroll to comment").
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToComment = () => {
    textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Focus after the smooth scroll has a moment to start, not before --
    // focusing immediately can cause some browsers to jump instantly instead.
    window.setTimeout(() => textareaRef.current?.focus(), 400);
  };

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await fetch('/api/client/outcomes');
      const json = (await res.json()) as { success: boolean; data?: OutcomeListItem[]; message?: string };
      if (!res.ok || !json.success) throw new Error(json.message ?? `HTTP ${res.status}`);
      setOutcomes(json.data ?? []);
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load outcomes.');
    } finally {
      setListLoading(false);
    }
  }, []);

  const fetchRepoOptions = useCallback(async () => {
    setRepoOptionsLoading(true);
    try {
      const res = await fetch('/api/client/outcomes/projects');
      const json = (await res.json()) as { success: boolean; data?: RepoOption[] };
      if (res.ok && json.success) setRepoOptions(json.data ?? []);
    } catch {
      // Non-fatal -- worst case, project sections and the "New outcome"
      // picker just don't have anything to show.
    } finally {
      setRepoOptionsLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (issueNumber: string, owner?: string | null, repo?: string | null) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const qs = owner && repo ? `?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}` : '';
      const res = await fetch(`/api/client/outcomes/${issueNumber}${qs}`);
      const json = (await res.json()) as { success: boolean; data?: OutcomeDetail; message?: string };
      if (!res.ok || !json.success) throw new Error(json.message ?? `HTTP ${res.status}`);
      setDetail(json.data ?? null);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Failed to load outcome.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
    fetchRepoOptions();
  }, [fetchList, fetchRepoOptions]);

  useEffect(() => {
    if (selectedIssue) {
      setDetail(null);
      setCommentText('');
      setCommentError(null);
      fetchDetail(selectedIssue, selectedOwner, selectedRepo);
    } else {
      setDetail(null);
    }
  }, [selectedIssue, selectedOwner, selectedRepo, fetchDetail]);

  const openOutcome = (item: OutcomeListItem) => {
    setPostActionWarning(null);
    setAddAttachmentOpen(false);
    setAttachmentFiles([]);
    setSearchParams({ issue: String(item.issue_number), owner: item.github_owner, repo: item.github_repo });
  };

  const backToList = () => {
    setPostActionWarning(null);
    setAddAttachmentOpen(false);
    setAttachmentFiles([]);
    setSearchParams({});
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIssue) return;

    const trimmed = commentText.trim();
    if (!trimmed) {
      setCommentError('Comment cannot be empty.');
      return;
    }

    setCommentSubmitting(true);
    setCommentError(null);
    try {
      const res = await fetch(`/api/client/outcomes/${selectedIssue}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comment_text: trimmed,
          github_owner: selectedOwner ?? undefined,
          github_repo: selectedRepo ?? undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !(json as { success?: boolean }).success) {
        throw new Error((json as { message?: string }).message ?? `HTTP ${res.status}`);
      }
      setCommentText('');
      await fetchDetail(selectedIssue, selectedOwner, selectedRepo);
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : 'Failed to add comment.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const openAddAttachment = () => {
    setAttachmentError(null);
    setAttachmentFiles([]);
    setAddAttachmentOpen(true);
  };

  const handleAddAttachment = async () => {
    if (!selectedIssue || attachmentFiles.length === 0) return;

    setAttachmentUploading(true);
    setAttachmentError(null);
    try {
      const formData = new FormData();
      formData.append('github_owner', selectedOwner ?? '');
      formData.append('github_repo', selectedRepo ?? '');
      attachmentFiles.forEach((f) => formData.append('file', f));

      const res = await fetch(`/api/client/outcomes/${selectedIssue}/attachments`, {
        method: 'POST',
        body: formData,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !(json as { success?: boolean }).success) {
        throw new Error((json as { message?: string }).message ?? `HTTP ${res.status}`);
      }
      setAttachmentFiles([]);
      setAddAttachmentOpen(false);
      await fetchDetail(selectedIssue, selectedOwner, selectedRepo);
    } catch (err) {
      setAttachmentError(err instanceof Error ? err.message : 'Failed to add attachment.');
    } finally {
      setAttachmentUploading(false);
    }
  };

  // ── New outcome (scoped to one project's section) ──────────────────────────
  const [newOutcomeProject, setNewOutcomeProject] = useState<{ project_id: string; project_name: string } | null>(null);
  const [selectedRepoKey, setSelectedRepoKey] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newSections, setNewSections] = useState<Record<string, string>>({});
  const [newAttachmentFiles, setNewAttachmentFiles] = useState<File[]>([]);
  const [newSubmitting, setNewSubmitting] = useState(false);
  const [newError, setNewError] = useState<string | null>(null);

  const openNewOutcome = (projectId: string, projectName: string) => {
    setNewError(null);
    setNewTitle('');
    setNewSections({});
    setNewAttachmentFiles([]);
    const projectRepos = repoOptions.filter((r) => r.project_id === projectId);
    setSelectedRepoKey(projectRepos.length === 1 ? repoOptionKey(projectRepos[0]) : '');
    setNewOutcomeProject({ project_id: projectId, project_name: projectName });
  };

  const handleCreateOutcome = async (e: React.FormEvent) => {
    e.preventDefault();
    const match = repoOptions.find((r) => repoOptionKey(r) === selectedRepoKey);
    if (!match) {
      setNewError('Please choose a repo.');
      return;
    }
    if (!newTitle.trim()) {
      setNewError('Title is required.');
      return;
    }

    setNewSubmitting(true);
    setNewError(null);
    try {
      const res = await fetch('/api/client/outcomes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: match.project_id,
          github_owner: match.github_owner,
          github_repo: match.github_repo,
          title: newTitle.trim(),
          sections: newSections,
        }),
      });
      const json = (await res.json()) as { success: boolean; data?: { issue_number: number }; message?: string };
      if (!res.ok || !json.success || !json.data) throw new Error(json.message ?? `HTTP ${res.status}`);

      const issueNumber = json.data.issue_number;

      // The outcome now exists and is never rolled back -- a failed
      // attachment upload is a separate, non-blocking warning shown on the
      // detail view we navigate to below, not a failure of this submission.
      let attachmentWarning: string | null = null;
      if (newAttachmentFiles.length > 0) {
        try {
          const attachFormData = new FormData();
          attachFormData.append('github_owner', match.github_owner);
          attachFormData.append('github_repo', match.github_repo);
          newAttachmentFiles.forEach((f) => attachFormData.append('file', f));
          const attRes = await fetch(`/api/client/outcomes/${issueNumber}/attachments`, {
            method: 'POST',
            body: attachFormData,
          });
          const attJson = await attRes.json().catch(() => ({}));
          if (!attRes.ok || !(attJson as { success?: boolean }).success) {
            attachmentWarning = `Outcome created, but attaching your file(s) failed: ${
              (attJson as { message?: string }).message ?? 'unknown error'
            }`;
          }
        } catch {
          attachmentWarning = 'Outcome created, but attaching your file(s) failed.';
        }
      }

      setNewOutcomeProject(null);
      setNewAttachmentFiles([]);
      setPostActionWarning(attachmentWarning);
      await fetchList();
      setSearchParams({ issue: String(issueNumber), owner: match.github_owner, repo: match.github_repo });
    } catch (err) {
      setNewError(err instanceof Error ? err.message : 'Failed to create outcome.');
    } finally {
      setNewSubmitting(false);
    }
  };

  // ── Detail view ──────────────────────────────────────────────────────────
  if (selectedIssue) {
    return (
      <div className="client-outcomes">
        <button type="button" className="client-outcomes-back" onClick={backToList}>
          ← Back to outcomes
        </button>

        {detailLoading && (
          <div className="client-outcomes-loading">
            <LoadingSpinner />
          </div>
        )}
        {detailError && !detailLoading && (
          <ErrorState message={detailError} onRetry={() => fetchDetail(selectedIssue, selectedOwner, selectedRepo)} />
        )}

        {detail && !detailLoading && !detailError && (
          <>
            {postActionWarning && (
              <div className="client-outcomes-error" role="alert">
                {postActionWarning}
              </div>
            )}

            <p className="client-outcomes-detail-project">{detail.project_name}</p>
            <h1 className="client-outcomes-detail-title">
              #{detail.issue_number} {detail.title}
            </h1>
            <Card className="client-outcomes-body">
              <div dangerouslySetInnerHTML={{ __html: renderSafeMarkdown(detail.body) }} />
            </Card>

            <Card className="client-outcomes-attachments">
              <div className="client-outcomes-attachments-head">
                <h2 className="client-outcomes-section-heading">Attachments</h2>
                {!addAttachmentOpen && (
                  <Button type="button" variant="secondary" size="sm" onClick={openAddAttachment}>
                    + Add attachment
                  </Button>
                )}
              </div>

              {detail.attachments.length === 0 && !addAttachmentOpen && (
                <p className="client-outcomes-empty">No attachments yet.</p>
              )}

              {detail.attachments.length > 0 && (
                <div className="client-outcomes-attachments-list">
                  {detail.attachments.map((a) => (
                    <div key={a.id} className="client-outcomes-attachment-row">
                      {isImageMime(a.mime_type) && (
                        <img
                          className="client-outcomes-attachment-thumb"
                          src={`/api/outcome-attachments/${a.id}/download`}
                          alt=""
                          loading="lazy"
                        />
                      )}
                      <div className="client-outcomes-attachment-meta">
                        <span className="client-outcomes-attachment-name">{a.file_name}</span>
                        <span className="client-outcomes-attachment-sub">
                          {formatFileSize(a.file_size_bytes)} · Added by {a.uploaded_by_name} · {formatDate(a.created_at)}
                        </span>
                      </div>
                      <a
                        className="client-outcomes-attachment-download"
                        href={`/api/outcome-attachments/${a.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {isInlineViewableMime(a.mime_type) ? 'View' : 'Download'}
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {addAttachmentOpen && (
                <div className="client-outcomes-attach-panel">
                  <input
                    type="file"
                    className="client-outcomes-file-input"
                    multiple
                    onChange={(e) => setAttachmentFiles(Array.from(e.target.files ?? []))}
                    disabled={attachmentUploading}
                  />
                  {attachmentFiles.length > 0 && (
                    <ul className="client-outcomes-staged-files">
                      {attachmentFiles.map((f, i) => (
                        <li key={`${f.name}-${i}`}>{f.name}</li>
                      ))}
                    </ul>
                  )}
                  {attachmentError && (
                    <div className="client-outcomes-error" role="alert">
                      {attachmentError}
                    </div>
                  )}
                  <div className="edit-actions">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={handleAddAttachment}
                      disabled={attachmentUploading || attachmentFiles.length === 0}
                    >
                      {attachmentUploading ? 'Uploading…' : 'Upload'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setAddAttachmentOpen(false)}
                      disabled={attachmentUploading}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            <h2 className="client-outcomes-section-heading">Comments</h2>
            {detail.comments.length === 0 && <p className="client-outcomes-empty">No comments yet.</p>}
            <div className="client-outcomes-comments">
              {detail.comments.map((c) => (
                <Card key={c.id} className="client-outcomes-comment">
                  <div className="client-outcomes-comment-meta">{formatDate(c.created_at)}</div>
                  <div dangerouslySetInnerHTML={{ __html: renderSafeMarkdown(c.body) }} />
                </Card>
              ))}
            </div>

            <form className="client-outcomes-comment-form" onSubmit={handleAddComment}>
              {commentError && (
                <div className="client-outcomes-error" role="alert">
                  {commentError}
                </div>
              )}
              <textarea
                ref={textareaRef}
                className="client-outcomes-textarea"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a comment…"
                rows={4}
                disabled={commentSubmitting}
              />
              <Button type="submit" variant="primary" disabled={commentSubmitting}>
                {commentSubmitting ? 'Posting…' : 'Post comment'}
              </Button>
            </form>

            <button
              type="button"
              className="client-outcomes-fab"
              onClick={scrollToComment}
              aria-label="Jump to add a comment"
            >
              Add comment
            </button>
          </>
        )}
      </div>
    );
  }

  // ── List view: one section per project, each a Kanban board ────────────────
  const distinctProjects = Array.from(new Map(repoOptions.map((r) => [r.project_id, r.project_name])).entries()).map(
    ([project_id, project_name]) => ({ project_id, project_name })
  );

  const newOutcomeRepos = newOutcomeProject ? repoOptions.filter((r) => r.project_id === newOutcomeProject.project_id) : [];

  return (
    <div className="client-outcomes">
      <h1 className="client-outcomes-title">Outcomes</h1>

      {(listLoading || repoOptionsLoading) && (
        <div className="client-outcomes-loading">
          <LoadingSpinner />
        </div>
      )}
      {listError && !listLoading && <ErrorState message={listError} onRetry={fetchList} />}

      {!listLoading && !listError && !repoOptionsLoading && distinctProjects.length === 0 && (
        <p className="client-outcomes-empty">No projects connected yet.</p>
      )}

      {!listLoading && !listError && !repoOptionsLoading &&
        distinctProjects.map((p) => (
          <ProjectSection
            key={p.project_id}
            projectId={p.project_id}
            projectName={p.project_name}
            items={(outcomes ?? []).filter((o) => o.project_id === p.project_id)}
            onSelect={openOutcome}
            onNewOutcome={openNewOutcome}
          />
        ))}

      <Modal
        isOpen={newOutcomeProject !== null}
        onClose={() => setNewOutcomeProject(null)}
        title={newOutcomeProject ? `New outcome — ${newOutcomeProject.project_name}` : 'New outcome'}
        size="lg"
        closeOnBackdropClick={false}
      >
        <form className="client-outcomes-new-form" onSubmit={handleCreateOutcome}>
          {newOutcomeRepos.length > 1 && (
            <div className="input-wrapper">
              <label className="input-label">Repo</label>
              <select className="input" value={selectedRepoKey} onChange={(e) => setSelectedRepoKey(e.target.value)}>
                <option value="">Select a repo…</option>
                {newOutcomeRepos.map((r) => (
                  <option key={repoOptionKey(r)} value={repoOptionKey(r)}>
                    {r.github_owner}/{r.github_repo}
                  </option>
                ))}
              </select>
            </div>
          )}

          <Input
            label="Title *"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="e.g. Add export to CSV"
            autoFocus
          />

          {OUTCOME_SECTION_ORDER.map((section) => (
            <div className="input-wrapper" key={section}>
              <label className="input-label">{section}</label>
              <textarea
                className="input client-outcomes-textarea"
                rows={2}
                value={newSections[section] ?? ''}
                onChange={(e) => setNewSections((prev) => ({ ...prev, [section]: e.target.value }))}
                placeholder="Optional"
              />
            </div>
          ))}

          <div className="input-wrapper">
            <label className="input-label">Attach files</label>
            <input
              type="file"
              className="client-outcomes-file-input"
              multiple
              onChange={(e) => setNewAttachmentFiles(Array.from(e.target.files ?? []))}
              disabled={newSubmitting}
            />
            {newAttachmentFiles.length > 0 && (
              <ul className="client-outcomes-staged-files">
                {newAttachmentFiles.map((f, i) => (
                  <li key={`${f.name}-${i}`}>{f.name}</li>
                ))}
              </ul>
            )}
          </div>

          {newError && (
            <div className="client-outcomes-error" role="alert">
              {newError}
            </div>
          )}

          <div className="edit-actions">
            <Button type="submit" variant="primary" disabled={newSubmitting}>
              {newSubmitting ? 'Creating…' : 'Create outcome'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setNewOutcomeProject(null)} disabled={newSubmitting}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ClientOutcomes;
